import { COGNITO_GROUP_NIKOLA_TEAM, useAuth } from "@assetwatch/auth";
import {
  Radio,
  RadioGroup,
  Stack,
  Switch,
  Text,
  Title,
  useTheme,
} from "@assetwatch/ui";
import { Card } from "@assetwatch/ui/experimental";
import type { ReactNode } from "react";
import { Trans, useTranslation } from "react-i18next";
import { EnterpriseSettingsPanelLanguage } from "./EnterpriseSettingsPanelLanguage";
import { pageHeaderStyles } from "./shared/pageHeader";

type SavingsMethodValue = "per-win" | "downtime" | "production";

const SAVINGS_METHOD: SavingsMethodValue = "per-win";
const monoClassName =
  "break-all font-[ui-monospace,SFMono-Regular,Menlo,Monaco,Consolas,monospace] text-(length:--font-size-body-md)";
const optionClassName =
  "flex cursor-not-allowed flex-col gap-(--spacing-8) rounded-(--radius-lg) border border-outline-subtle bg-surface p-(--spacing-16)";

function MonoToken({ children }: { children?: ReactNode }) {
  return <span className={monoClassName}>{children}</span>;
}

function StrongText({ children }: { children?: ReactNode }) {
  return <strong>{children}</strong>;
}

const OPTIONS: Array<{
  value: SavingsMethodValue;
  titleKey: string;
  recommended?: boolean;
  bodyKey: string;
  exampleKey: string;
}> = [
  {
    value: "per-win",
    titleKey: "enterprise.settings.savingsMethod.perWinTitle",
    recommended: true,
    bodyKey: "enterprise.settings.savingsMethod.perWinDescription",
    exampleKey: "enterprise.settings.savingsMethod.perWinExample",
  },
  {
    value: "downtime",
    titleKey: "enterprise.settings.savingsMethod.downtimeTitle",
    bodyKey: "enterprise.settings.savingsMethod.downtimeDescription",
    exampleKey: "enterprise.settings.savingsMethod.downtimeExample",
  },
  {
    value: "production",
    titleKey: "enterprise.settings.savingsMethod.productionTitle",
    bodyKey: "enterprise.settings.savingsMethod.productionDescription",
    exampleKey: "enterprise.settings.savingsMethod.productionExample",
  },
];

export function EnterpriseSettingsPanel() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { theme, toggleTheme } = useTheme();

  return (
    <Stack gap={20}>
      <header
        className={pageHeaderStyles.pageHeader}
        data-testid="settings-page-heading"
      >
        <Stack gap={12}>
          <h1 className={pageHeaderStyles.pageTitle}>
            {t("enterprise.settings.headerTitle")}
          </h1>
          <div className={pageHeaderStyles.pageIntro}>
            <Text>{t("enterprise.settings.headerDescription")}</Text>
          </div>
        </Stack>
      </header>

      <Card data-testid="settings-appearance-card">
        <Stack gap={12} align="start">
          <Title size="md" as="h2">
            {t("enterprise.settings.appearanceTitle")}
          </Title>
          <Text size="sm" tone="subtle">
            {t("enterprise.settings.appearanceDescription")}
          </Text>
          <Switch
            label={t("enterprise.settings.appearanceDarkThemeLabel")}
            description={t(
              "enterprise.settings.appearanceDarkThemeDescription",
            )}
            checked={theme === "dark"}
            onCheckedChange={() => toggleTheme()}
            data-testid="settings-theme-toggle"
          />
        </Stack>
      </Card>

      <EnterpriseSettingsPanelLanguage />

      <Card data-testid="settings-session-debug">
        <Stack gap={8}>
          <Title size="md" as="h2">
            {t("enterprise.settings.sessionDebugTitle")}
          </Title>
          <Text size="sm" tone="subtle">
            <Trans
              i18nKey="enterprise.settings.sessionDebugDescription"
              components={{
                authProvider: <MonoToken />,
                roleKey: <MonoToken />,
                groupsKey: <MonoToken />,
                userGroupKey: <MonoToken />,
              }}
            />
          </Text>
          <Text size="sm">
            <strong>{t("enterprise.settings.sessionDebugEmailLabel")}:</strong>{" "}
            {user?.email ?? "—"}
          </Text>
          <Text size="sm">
            <strong>{t("enterprise.settings.sessionDebugRoleLabel")}:</strong>{" "}
            {user?.role?.trim() ? (
              <span className={monoClassName}>{user.role}</span>
            ) : (
              <em>{t("enterprise.settings.sessionDebugEmptyValue")}</em>
            )}
          </Text>
          <Text size="sm">
            <strong>{t("enterprise.settings.sessionDebugGroupsLabel")}:</strong>{" "}
            {user?.cognitoGroups?.length ? (
              <span className={monoClassName}>
                [{user.cognitoGroups.map((g) => `"${g}"`).join(", ")}]
              </span>
            ) : (
              <em>{t("enterprise.settings.sessionDebugNoneValue")}</em>
            )}
          </Text>
          <Text size="sm">
            <strong>
              {t("enterprise.settings.sessionDebugNikolaTeamLabel")}:
            </strong>{" "}
            {(user?.cognitoGroups ?? []).includes(COGNITO_GROUP_NIKOLA_TEAM) ? (
              <span className={monoClassName}>true</span>
            ) : (
              <span className={monoClassName}>false</span>
            )}
          </Text>
        </Stack>
      </Card>

      <Card data-testid="settings-cost-savings-fieldset">
        <Stack gap={16}>
          <Title size="lg" as="h2">
            {t("enterprise.settings.savingsMethod.title")}
          </Title>
          <Text>{t("enterprise.settings.savingsMethod.description")}</Text>

          <RadioGroup
            aria-label={t("enterprise.settings.savingsMethod.ariaLabel")}
            value={SAVINGS_METHOD}
            disabled
          >
            {OPTIONS.map((opt) => {
              const isSelected = opt.value === SAVINGS_METHOD;
              return (
                <div
                  key={opt.value}
                  className={
                    isSelected
                      ? `${optionClassName} border-primary/[0.42] bg-primary/[0.08] [html.dark_&]:border-primary/[0.5] [html.dark_&]:bg-primary/[0.12]`
                      : optionClassName
                  }
                  data-testid={`settings-cost-savings-${opt.value}`}
                >
                  <Radio
                    value={opt.value}
                    label={t(opt.titleKey)}
                    disabled
                    description={t(opt.bodyKey)}
                  />
                  <div className="flex min-w-0 flex-col gap-(--spacing-6) pl-(--spacing-24)">
                    {opt.recommended ? (
                      <Text as="span" size="sm" tone="subtle">
                        <span className="font-medium text-text-subtle">
                          {t("enterprise.settings.savingsMethod.recommended")}
                        </span>
                      </Text>
                    ) : null}
                    <span className="mt-(--spacing-2) block">
                      <Text as="span" size="sm" tone="subtle">
                        <strong>
                          {t("enterprise.settings.savingsMethod.exampleLabel")}
                        </strong>{" "}
                        {t(opt.exampleKey)}
                      </Text>
                    </span>
                  </div>
                </div>
              );
            })}
          </RadioGroup>

          <Text size="sm" tone="subtle">
            <Trans
              i18nKey="enterprise.settings.savingsMethod.unavailableNotice"
              values={{
                method: t("enterprise.settings.savingsMethod.perWinTitle"),
              }}
              components={{ method: <StrongText /> }}
            />
          </Text>
        </Stack>
      </Card>
    </Stack>
  );
}
